import { actions, raise, sendTo } from "xstate"
import * as F from "fp-ts/function"
import * as O from "fp-ts/Option"
import { FarePriceRequest } from "@ssc-booking/api-contracts"
import { RequestResponseChannel } from "../../../../../_common/machines/data-load-machine"
import { assertEvent } from "../../../../../_common/xstate-helpers"
import { FareCard, FareCardIndex } from "../../../read-models/fare-card"
import { vsMember } from "../../../vs-code"
import { maybeFareCardByIndex } from "../../../read-models/fare"
import { ECommerceEvent, ECommerceStateNodeConfig } from "../e-commerce-machine"
import {
    buildFarePriceLoader,
    FarePriceLoaderActorRef,
    farePriceLoaderId,
} from "../data-loading/fare-price-loader-machine"
import { ECommerceContext } from "../e-commerce-machine-context"

export const allFarePricesState = (websocketClient: RequestResponseChannel): ECommerceStateNodeConfig => ({
    type: "parallel",
    states: [0, 1, 2, 3]
        .filter(FareCardIndex.is)
        .map((index) => ({
            [farePriceLoaderStateId(index)]: farePriceLoaderState(websocketClient, index),
        }))
        .reduce((s, a) => Object.assign(s, a), {}),
    on: {
        FARE_PRICE_LOADED: { actions: ["updateFareCardAsGood"] },
        FARE_PRICE_ERROR: { actions: ["updateFareCardAsError"] },
    },
})

const farePriceLoaderState = (
    websocketClient: RequestResponseChannel,
    index: FareCardIndex,
): ECommerceStateNodeConfig => {
    type Context = ECommerceContext
    type Events = ECommerceEvent

    return {
        id: farePriceLoaderStateId(index),
        invoke: {
            id: farePriceLoaderId(index),
            src: buildFarePriceLoader(websocketClient, index),
        },
        on: {
            LOAD_FARE_PRICE: {
                /*cond: (ctx, event, condMeta: GuardMeta<Context, Events>) =>
                    event.index === index && isNotCheckingOut(ctx, event, condMeta),*/

                cond: (ctx, event, meta) =>
                    event.index === index &&
                    // NOTE: inline of isNotCheckingOut(ctx, event, condMeta) but type checking fails
                    !(meta.state.matches("flow.checkout.validated") || meta.state.matches("flow.readyToCheckout")),

                actions: [
                    actions.pure<Context, Events>((ctx) =>
                        F.pipe(
                            maybeFareCardByIndex(ctx, index),
                            O.foldW(F.constUndefined, (fareCard) =>
                                sendTo<Context, Events, FarePriceLoaderActorRef>(
                                    farePriceLoaderId(index),
                                    (ctx, ev) => {
                                        assertEvent(ev, "LOAD_FARE_PRICE")
                                        return {
                                            type: "LOAD",
                                            data: farePriceRequest(ctx, fareCard),
                                            country: ctx.geoLocation.countryCode,
                                        }
                                    },
                                ),
                            ),
                        ),
                    ),
                ],
            },
            FARE_CARDS_LOADED: {
                cond: (_, ev) => ev.fareCards.some((x) => x.index === index),
                actions: [raise({ type: "LOAD_FARE_PRICE", index })],
            },
            FARE_CARD_CHANGED: {
                cond: (_, ev) => ev.fareCard.index === index,
                actions: [raise({ type: "LOAD_FARE_PRICE", index })],
            },
            VS_CODE_CHANGED: {
                cond: (ctx) => ctx.fareCards.find((x) => x.index === index)?.options.vs._tag == "Some",
                actions: [raise({ type: "LOAD_FARE_PRICE", index })],
            },
        },
    }
}

const farePriceLoaderStateId = (index: number): string => `${farePriceLoaderId(index)}_state`

const farePriceRequest = (ctx: ECommerceContext, fareCard: FareCard): FarePriceRequest => ({
    cruiseCode: ctx.cruiseCode,
    occupancy: ctx.guests,
    fareCode: fareCard.fare,
    suiteCategory: O.none,
    vsMember: O.some(vsMember(ctx.vsCode, fareCard.options.vs)),
    availabilities: ctx.flowVersion.availabilities,
    air: fareCard.air,
})
